import React, { FC, useEffect } from 'react'
import { Alert, Form, Input } from 'antd'
import { FormInstance } from 'antd/es/form/hooks/useForm'

import '../index.less'
import { MONA_KEY_INDENTIFIER, useSetPractitionerMonaApiKeyMutation } from '../../../../core/api/interopApi'
import { useGetAndTryDecryptMainAttachmentAsJsonQuery } from '../../../../core/api/documentApi'
import { skipToken } from '@reduxjs/toolkit/query'
import { SpinLoader } from '../../../common/SpinLoader'
import { useGetPractitionerQuery } from '../../../../core/api/practitionerApi'
import { findPropertyValue } from '../../../../helpers/property'

interface MonaConnectionProps {
  form: FormInstance
  practitionerId?: string
  onClose: () => void
  t: (key: string, ctxt?: { [key: string]: string }) => string
}

export const MonaConnection: FC<MonaConnectionProps> = ({ form, practitionerId, onClose, t }) => {
  const [setPractitionerMonaKey, { isSuccess: isMonaKeySetSuccessfully, isLoading: isSetPractitionerMonaKeyLoading }] = useSetPractitionerMonaApiKeyMutation()
  const { data: practitioner, isFetching: isPractitionerFetching } = useGetPractitionerQuery(practitionerId ?? skipToken, {
    refetchOnMountOrArgChange: true,
    refetchOnFocus: true,
    refetchOnReconnect: true,
  })
  const existingMonaDocumentId: string | undefined = findPropertyValue(practitioner?.properties, MONA_KEY_INDENTIFIER)
  const { data: decryptedMonaKeyDocument, isFetching: isDecryptedMonaKeyDocumentLoading } = useGetAndTryDecryptMainAttachmentAsJsonQuery(existingMonaDocumentId ?? skipToken, {
    refetchOnMountOrArgChange: true, // re-fetch even if cached
    refetchOnFocus: true, // optional: also refetch on window focus
    refetchOnReconnect: true, // optional: refetch after reconnect
  })

  useEffect(() => {
    if (isMonaKeySetSuccessfully) {
      onClose()
    }
  }, [isMonaKeySetSuccessfully])

  const handleSubmit = async (values: { monaApiKey: string }) => {
    const { monaApiKey } = values
    if (practitioner) {
      setPractitionerMonaKey({ practitioner, monaApiKey })
    } else {
      throw new Error('Practitioner is required to set Mona API key')
    }
  }

  const isLoading = isSetPractitionerMonaKeyLoading || isDecryptedMonaKeyDocumentLoading || isPractitionerFetching

  if (isLoading) {
    return (
      <div className="modalManageAccountForm__form">
        <SpinLoader />
      </div>
    )
  }

  return (
    <Form
      className="modalManageAccountForm__form"
      layout="vertical"
      onFinish={async (values) => {
        await handleSubmit(values)
      }}
      colon={false}
      form={form}
      initialValues={{ monaApiKey: decryptedMonaKeyDocument?.monaApiKey }}
    >
      <Alert message={t('modalManageAccountForm.mona.alert')} type="info" showIcon={true} style={{ width: '100%' }} />
      <div className="modalManageAccountForm__form__inputs">
        <Form.Item name="monaApiKey" label={t('modalManageAccountForm.mona.apiKey')} rules={[{ required: true, message: t('modalManageAccountForm.mona.apiKeyIsRequired') }]}>
          <Input placeholder={t('modalManageAccountForm.mona.apiKey')} size="large" />
        </Form.Item>
      </div>
    </Form>
  )
}
